import { Button, Col, Form, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';

import { EditNumber } from 'components/forms/EditNumber';
import { EditSelect } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { EditText } from 'components/forms/EditText';
import { EditTime } from 'components/forms/EditTime';
import { EditSchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';

import { EditDateBound } from './EditDateBound';
import type { EditableSchedule } from './EditableSchedule';
import { DAY_OF_WEEK_OPTIONS, MONTH_OPTIONS, NTH_OF_MONTH_OPTIONS, WEEK_OF_MONTH_OPTIONS } from './scheduleFieldOptions';

interface Props {
  readonly schedule: EditableSchedule;
}

/**
 * The fields of a single opening rule. A pure `observer` view: every control reads a
 * field and assigns it back, with no local state and no validation — the server owns
 * the rules and the dialog surfaces its message.
 *
 * The fields narrow *independently*, and a rule opens the window only where all of
 * the ones that are set agree. Leaving them all empty is meaningful: it opens the
 * window all the time.
 *
 * The nth-of-month and cross-window fields sit behind a toggle. They are rarely used
 * and they dominate the form when they are not — but it starts open whenever the rule
 * already uses one, so nothing an operator has to maintain is hidden from them (see
 * `EditableSchedule.showAdvanced`).
 */
export const ScheduleRuleFields = observer<Props>(({ schedule }) => (
  <>
    <Row className="mb-3">
      <Col md={12}>
        <EditString label="Name" value={schedule.name} onChange={(v) => (schedule.name = v)} />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={12}>
        <EditText label="Description" value={schedule.description} onChange={(v) => (schedule.description = v)} />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={12}>
        <EditTimeZoneReference
          label="Timezone"
          value={schedule.timezone}
          onChange={(v) => (schedule.timezone = v)}
          // Empty is the meaning-carrying state here — it is what says the rule
          // follows its window — so the field says so itself rather than leaving
          // the operator to infer it from a blank.
          placeholder={schedule.schedulingWindow.timezone === null ? "use the window's timezone" : `use ${schedule.schedulingWindow.timezone} from the window`}
        />
        <Form.Text className="text-muted">Leave empty to use the timezone from the window.</Form.Text>
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={6}>
        <EditTime label="Opens at" value={schedule.startTime} onChange={(v) => (schedule.startTime = v)} seconds />
      </Col>
      <Col md={6}>
        <EditTime label="Closes at" value={schedule.endTime} onChange={(v) => (schedule.endTime = v)} seconds />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={6}>
        <EditDateBound
          label="From date"
          date={schedule.dateFrom}
          onDateChange={(v) => (schedule.dateFrom = v)}
          hasTime={schedule.dateFromHasTime}
          onHasTimeChange={(v) => (schedule.dateFromHasTime = v)}
          time={schedule.dateFromTime}
          onTimeChange={(v) => (schedule.dateFromTime = v)}
          timeLabel="Set the opening time on the first day"
          timeInfo="Overrides 'Opens at' on the first day only, so a range can start part-way through a day."
          defaultTime="08:00:00"
        />
      </Col>
      <Col md={6}>
        <EditDateBound
          label="Until date"
          date={schedule.dateUntil}
          onDateChange={(v) => (schedule.dateUntil = v)}
          hasTime={schedule.dateUntilHasTime}
          onHasTimeChange={(v) => (schedule.dateUntilHasTime = v)}
          time={schedule.dateUntilTime}
          onTimeChange={(v) => (schedule.dateUntilTime = v)}
          timeLabel="Set the closing time on the last day"
          timeInfo="Overrides 'Closes at' on the last day only, so a range can end part-way through a day."
          defaultTime="16:00:00"
        />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={6}>
        <EditSelect<number | null> label="From month" value={schedule.monthFrom} onChange={(v) => (schedule.monthFrom = v)} options={MONTH_OPTIONS} />
      </Col>
      <Col md={6}>
        <EditSelect<number | null> label="Until month" value={schedule.monthUntil} onChange={(v) => (schedule.monthUntil = v)} options={MONTH_OPTIONS} />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={6}>
        <EditSelect<number | null>
          label="From week of month"
          value={schedule.weekOfMonthFrom}
          onChange={(v) => (schedule.weekOfMonthFrom = v)}
          options={WEEK_OF_MONTH_OPTIONS}
        />
      </Col>
      <Col md={6}>
        <EditSelect<number | null>
          label="Until week of month"
          value={schedule.weekOfMonthUntil}
          onChange={(v) => (schedule.weekOfMonthUntil = v)}
          options={WEEK_OF_MONTH_OPTIONS}
        />
      </Col>
    </Row>

    <Row className="mb-3">
      <Col md={6}>
        <EditSelect<number | null>
          label="From day of week"
          value={schedule.dayOfWeekFrom}
          onChange={(v) => (schedule.dayOfWeekFrom = v)}
          options={DAY_OF_WEEK_OPTIONS}
        />
      </Col>
      <Col md={6}>
        <EditSelect<number | null>
          label="Until day of week"
          value={schedule.dayOfWeekUntil}
          onChange={(v) => (schedule.dayOfWeekUntil = v)}
          options={DAY_OF_WEEK_OPTIONS}
        />
      </Col>
    </Row>

    <Button variant="link" className="px-0" onClick={() => (schedule.showAdvanced = !schedule.showAdvanced)} aria-expanded={schedule.showAdvanced}>
      {schedule.showAdvanced ? 'Hide advanced' : 'Show advanced'}
    </Button>

    {schedule.showAdvanced && (
      <>
        <Row className="mb-3 mt-2">
          <Col md={6}>
            <EditSelect<number | null>
              label="From nth of month"
              value={schedule.nthOfMonthFrom}
              onChange={(v) => (schedule.nthOfMonthFrom = v)}
              options={NTH_OF_MONTH_OPTIONS}
            />
          </Col>
          <Col md={6}>
            <EditSelect<number | null>
              label="Until nth of month"
              value={schedule.nthOfMonthUntil}
              onChange={(v) => (schedule.nthOfMonthUntil = v)}
              options={NTH_OF_MONTH_OPTIONS}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <EditSchedulingWindowReference
              label="Count nth-of-month within"
              value={schedule.nthOfMonthFromInWindow}
              onChange={(v) => (schedule.nthOfMonthFromInWindow = v)}
            />
          </Col>
          <Col md={6}>
            <EditSchedulingWindowReference
              label="Count nth-of-month until within"
              value={schedule.nthOfMonthUntilInWindow}
              onChange={(v) => (schedule.nthOfMonthUntilInWindow = v)}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <EditSchedulingWindowReference
              label="Shift if closed in"
              value={schedule.shiftIfClosedWindow}
              onChange={(v) => (schedule.shiftIfClosedWindow = v)}
            />
          </Col>
          <Col md={6}>
            <EditNumber label="Shift by open days" value={schedule.shiftOpenDays} onChange={(v) => (schedule.shiftOpenDays = v)} />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <EditSchedulingWindowReference label="Only while open in" value={schedule.openDuringWindow} onChange={(v) => (schedule.openDuringWindow = v)} />
          </Col>
          <Col md={6}>
            <EditSchedulingWindowReference label="Not while open in" value={schedule.closedDuringWindow} onChange={(v) => (schedule.closedDuringWindow = v)} />
          </Col>
        </Row>
      </>
    )}
  </>
));
